import { useState } from "react";
import { Notification, useMessagedAsyncRetry } from "@payping/web-components";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { terminalManagerApis } from "@payping/api";
import { IBANItem, IBANStatus } from "../types";
import { convertApiResponseToIBANItem } from "../utils/apiConverters";

export const useIBANList = () => {
  const { t } = useAppTranslation();
  const [approvedIBANList, setApprovedIBANList] = useState<IBANItem[]>([]);
  const [pendingIBANList, setPendingIBANList] = useState<IBANItem[]>([]);
  const [rejectedIBANList, setRejectedIBANList] = useState<IBANItem[]>([]);

  const [currentPage] = useState(1);
  const [pageSize] = useState(10);

  const [notification, contextHolder] = Notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });

  const getIbansState = useMessagedAsyncRetry(
    async () => {
      try {
        const response = await terminalManagerApis.getIbans(currentPage, pageSize);

        const convertedItems = response?.list?.items?.map(convertApiResponseToIBANItem) || [];
        setApprovedIBANList(convertedItems.filter((item: IBANItem) => item.status === IBANStatus.APPROVED));
        setPendingIBANList(
          convertedItems.filter(
            (item: IBANItem) => item.status === IBANStatus.REQUESTED || item.status === IBANStatus.IN_PROGRESS,
          ),
        );
        setRejectedIBANList(convertedItems.filter((item: IBANItem) => item.status === IBANStatus.REJECTED));
        return response;
      } catch (error) {
        notification.error({
          message: t("profile.bank.errorLoadingIBANs"),
          closable: true,
        });
        return null;
      }
    },
    [currentPage, pageSize],
    { success: false, error: true },
  );

  const handleSettlementSelection = (ibanId: string) => {
    setApprovedIBANList((prev) =>
      prev.map((item) => {
        const isTargetItem = item.id === ibanId;
        const wasActiveSettlement = item.status === IBANStatus.ACTIVE_SETTLEMENT;

        let newStatus = item.status;
        if (isTargetItem) {
          newStatus = IBANStatus.ACTIVE_SETTLEMENT;
        } else if (wasActiveSettlement) {
          newStatus = IBANStatus.SETTLEMENT_READY;
        }

        return {
          ...item,
          isActiveSettlement: isTargetItem,
          status: newStatus,
        };
      }),
    );
  };

  return {
    approvedIBANList,
    pendingIBANList,
    rejectedIBANList,
    getIbansState,
    handleSettlementSelection,
    contextHolder,
  };
};

